// Third Party
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

// Namespace
import * as ProposalNS from '../../../Proposal';

// Components
import { CloseButton } from '@/views/components/buttons';
import FullPageModal from '@/views/components/modals/FullPageModal';
import Modal from '@/views/components/modals/AccessibleModal';
import { PrimaryNavigation } from '@/views/components/navigation';
import OptionsSidebar from './OptionsSidebar';
import CustomizeSidebar from './CustomizeSidebar';

// Types
import NavigationItem from '@/types/navigation-item';
import Comparison from '@/types/comparison';
import User from '@/types/user';
import Locale from '@/types/locale';

// Utils
import { FormattedMessage } from '@/utils/i18n';
import { getSidebarNavigationTabs } from '@/modules/proposal/ProposalHelpers';
import useScreenSize from '@/hooks/useScreenSize';
import TabNavigation from '../TabNavigation';

type EditProposalModalProps = {
  user: User;
  locale: Locale;
  template: ProposalNS.ProposalTemplateV2;
  proposal: ProposalNS.Proposal;
  proposalTemplateOptions: ProposalNS.ProposalTemplateOptions;
  open: boolean;
  setOpen: (open: boolean) => void;
  comparison: Comparison;
  gifHideBaseScenario: boolean | undefined;
};

const EditProposalModal = ({
  user,
  template,
  proposal,
  proposalTemplateOptions,
  open,
  setOpen,
  comparison,
  locale,
  gifHideBaseScenario,
}: EditProposalModalProps) => {
  const tabs = getSidebarNavigationTabs(user, template);
  const [selectedSidebarTab, setSelectedSidebarTab] = useState<string | undefined>(tabs[0]?.id || undefined);
  const [sidebarToDisplay, setSidebarToDisplay] = useState<ProposalNS.ProposalSidebarTab | undefined>(undefined);
  const { isDesktop } = useScreenSize();

  if (isDesktop) {
    setOpen(false);
  }

  useEffect(() => {
    if (selectedSidebarTab) {
      const sidebarTab = template.sidebarTabs.find(tab => tab.key === selectedSidebarTab);
      setSidebarToDisplay(sidebarTab);
    } else {
      setSidebarToDisplay(undefined);
    }
  }, [template.sidebarTabs, selectedSidebarTab]);

  const onTabChange = (tab: string | undefined) => {
    setSelectedSidebarTab(tab);
  };

  return (
    <FullPageModal show={open} onExited={() => setOpen(false)} className="proposal-edit-modal">
      <Modal.Header className="proposal-edit-modal-header">
        <Modal.Title>
          <FormattedMessage id="Common.Edit" />
        </Modal.Title>
        <CloseButton onClick={() => setOpen(false)} />
      </Modal.Header>
      <Modal.Body>
        <TabNavigation className="proposal-tabs" sidebarTabs={tabs} selectedSidebarTab={selectedSidebarTab} onTabChange={onTabChange} />
        <div className="sidebar-tab-container">
          {sidebarToDisplay?.key === ProposalNS.SidebarTabs.CUSTOMIZE && (
            <CustomizeSidebar
              sidebar={sidebarToDisplay}
              user={user}
              template={template}
              proposal={proposal}
              comparison={comparison}
              locale={locale}
              proposalTemplateOptions={proposalTemplateOptions}
              gifHideBaseScenario={gifHideBaseScenario}
            />
          )}
          {sidebarToDisplay?.key === ProposalNS.SidebarTabs.OPTIONS && (
            <OptionsSidebar user={user} sidebar={sidebarToDisplay} proposal={proposal} proposalTemplateOptions={proposalTemplateOptions} />
          )}
        </div>
      </Modal.Body>
    </FullPageModal>
  );
};

export default EditProposalModal;
